import React, { useEffect, useState } from 'react';
import { FileLock2, Clock, Download } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import {
  fetchMyFileSends,
  loadShareUrl,
  buildShareUrl,
  decryptSecretForUser,
} from '../../file-send';

interface ComposeFileSendSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onInsert: (url: string, filename: string) => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const past = diffMs < 0;
  const mins = Math.round(Math.abs(diffMs) / 60000);
  const value =
    mins < 60
      ? `${Math.max(1, mins)} min`
      : mins < 1440
        ? `${Math.round(mins / 60)} h`
        : `${Math.round(mins / 1440)} d`;
  return past ? `${value} ago` : `in ${value}`;
}

const ComposeFileSendSidebar: React.FC<ComposeFileSendSidebarProps> = ({
  isOpen,
  onClose,
  onInsert,
}) => {
  const { colors } = useTheme();
  const { user } = useAuth();
  const userId = user?.id || 0;
  
  const [shares, setShares] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    
    const loadShares = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await fetchMyFileSends();
        
        // Decrypt secrets for URLs
        const rowsWithUrls = await Promise.all(
          rows.map(async (row) => {
            let shareUrl = loadShareUrl(row.id);
            
            if (!shareUrl && row.encryptedSecret && userId) {
              try {
                const secretFragment = await decryptSecretForUser(row.encryptedSecret, userId);
                shareUrl = buildShareUrl(window.location.origin, row.id, secretFragment);
              } catch (e) {
                console.warn(`Failed to decrypt secret for ${row.id}:`, e);
              }
            }
            
            return { ...row, shareUrl };
          })
        );
        
        // Filter only active links with URLs
        setShares(rowsWithUrls.filter((row: any) => row.active && row.shareUrl));
      } catch (e) {
        setError((e as Error)?.message || 'Failed to load links');
      } finally {
        setLoading(false);
      }
    };
    
    void loadShares();
  }, [isOpen, userId]);

  const handleInsert = (url: string, filename: string) => {
    onInsert(url, filename);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <aside
      className={cn(
        'flex flex-col h-full min-h-0 self-stretch w-[min(100vw,20rem)] flex-shrink-0 border-r',
        colors.border,
        colors.surface,
      )}
      aria-label="Secure file links"
    >
      {/* Header */}
      <div className={cn('flex items-center justify-between gap-1 px-1.5 py-1 border-b flex-shrink-0', colors.border)}>
        <span className={cn('text-[11px] font-semibold', colors.text)}>Secure File Links</span>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
          title="Close"
          aria-label="Close secure file links"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
        <p className={cn('px-1 py-1.5 text-[10px]', colors.textMuted)}>
          Click a link to insert it into your email
        </p>

          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : error ? (
            <div className={cn('px-1 py-2 text-[10px] text-red-600 dark:text-red-400')}>
              {error}
            </div>
          ) : shares.length === 0 ? (
            <p className={cn('px-1 py-2 text-[10px]', colors.textMuted)}>No active links.</p>
          ) : (
            <ul>
              {shares.map((row: any) => {
                const name = row.originalFilename || 'Encrypted file';

                return (
                  <li key={row.id} className={cn('border-b last:border-b-0 py-1.5', colors.border)}>
                    <button
                      type="button"
                      onClick={() => handleInsert(row.shareUrl, name)}
                      className={cn(
                        'group w-full text-left rounded px-1 py-1 text-[11px] leading-tight transition-colors',
                        colors.text,
                        'hover:bg-purple-50 hover:text-purple-900 dark:hover:bg-purple-950/40 dark:hover:text-purple-100',
                        'focus:outline-none focus-visible:ring-1 focus-visible:ring-purple-500/50',
                      )}
                      title={`Insert ${name}`}
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <FileLock2 className="w-3.5 h-3.5 flex-shrink-0 text-purple-600 dark:text-purple-400" strokeWidth={1.75} aria-hidden />
                          <span className="truncate font-medium">{name}</span>
                          <span className={cn('text-[9px] px-1 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ml-auto flex-shrink-0')}>
                            Active
                          </span>
                        </div>
                        
                        <div className={cn('flex items-center gap-2 text-[10px]', colors.textMuted)}>
                          <span>{formatBytes(row.sizeBytes)}</span>
                          <span className="flex items-center gap-0.5">
                            <Download className="h-2.5 w-2.5" aria-hidden />
                            {row.downloadCount}/{row.maxDownloads}
                          </span>
                        </div>
                        
                        <div className={cn('flex items-center gap-1 text-[10px]', colors.textMuted)}>
                          <Clock className="h-2.5 w-2.5" aria-hidden />
                          <span>{formatRelative(row.expiresAt)}</span>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
      </div>

      {/* Footer */}
      <div className={cn('flex-shrink-0 border-t px-1 py-1.5', colors.border)}>
        <button
          type="button"
          onClick={() => {
            window.open('/file-send', '_blank');
            onClose();
          }}
          className={cn(
            'w-full text-left rounded px-1.5 py-1.5 text-[11px] font-medium flex items-center gap-1.5 transition-colors',
            colors.text,
            'hover:bg-black/5 dark:hover:bg-white/5',
          )}
        >
          <FileLock2 className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.75} aria-hidden />
          Create New Link
        </button>
      </div>
    </aside>
  );
};

export default ComposeFileSendSidebar;
